import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { contractService, propertyService, userService } from '../../../services/api'

const EMPTY_FORM_DATA = {
  property_id: '',
  tenant_id: '',
  start_date: '',
  end_date: '',
  contractDate: '',
  monthly_rent: '',
  security_deposit: '',
  payment_frequency: 'monthly',
  payment_day: 1,
  terms_conditions: '',
  special_conditions: '',
  nombresApellidosArrendador: '',
  contractDomicile: '',
  propertyPurpose: '',
  increasePercentage: '',
  propertyInstallations: '',
  tenantDocumentNumber: '',
  debtorFullName: '',
  debtorDocumentNumber: '',
  debtorIssuePlace: '',
  debtorAddress: '',
  debtor2FullName: '',
  debtor2DocumentNumber: '',
  debtor2IssuePlace: '',
  debtor2Address: '',
  landlordNotificationAddress: '',
  signatureDate: ''
}

const PAYMENT_DAY_WORDS = {
  1: 'uno',
  2: 'dos',
  3: 'tres',
  4: 'cuatro',
  5: 'cinco',
  6: 'seis',
  7: 'siete',
  8: 'ocho',
  9: 'nueve',
  10: 'diez',
  11: 'once',
  12: 'doce',
  13: 'trece',
  14: 'catorce',
  15: 'quince',
  16: 'dieciséis',
  17: 'diecisiete',
  18: 'dieciocho',
  19: 'diecinueve',
  20: 'veinte',
  21: 'veintiuno',
  22: 'veintidós',
  23: 'veintitrés',
  24: 'veinticuatro',
  25: 'veinticinco',
  26: 'veintiséis',
  27: 'veintisiete',
  28: 'veintiocho'
}

const toInputDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0]
}

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '')

const formatSpanishDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const month = capitalize(new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(date))
  return `${month} ${date.getDate()} de ${date.getFullYear()}`
}

const formatContractPeriod = (startDate, endDate) => {
  const start = formatSpanishDate(startDate)
  const end = formatSpanishDate(endDate)
  if (!start && !end) return ''
  if (!start) return end
  if (!end) return start
  return `${start} hasta ${end}`
}

const calculateMonthDifference = (startDate, endDate) => {
  if (!startDate || !endDate) return ''

  const start = new Date(startDate)
  const end = new Date(endDate)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ''

  const rawMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
  if (end.getDate() < start.getDate()) {
    return Math.max(rawMonths - 1, 0)
  }

  return Math.max(rawMonths, 0)
}

const getFullName = (person) => {
  if (!person) return ''

  const firstName = person.first_name || person.firstName || ''
  const lastName = person.last_name || person.lastName || ''
  const name = person.name || `${firstName} ${lastName}`.trim()
  return name.trim()
}

const getPropertyLocation = (property) => {
  if (!property) return ''

  const parts = [property.address, property.city, property.state].filter(Boolean)
  return parts.join(', ')
}

const getPaymentDayText = (value) => {
  const numericValue = Number(value)
  return PAYMENT_DAY_WORDS[numericValue] || ''
}

const ReadOnlyField = ({ label, value, note }) => (
  <div>
    <p className="text-sm font-medium text-slate-700">{label}</p>
    <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
      {value || 'Pendiente'}
    </div>
    {note ? <p className="mt-2 text-xs leading-5 text-slate-500">{note}</p> : null}
  </div>
)

const ContractForm = () => {
  const { id } = useParams()  // Get contract ID from URL
  const navigate = useNavigate()
  const isEdit = Boolean(id)  // Check if we're editing
  const [formData, setFormData] = useState({
    ...EMPTY_FORM_DATA,
    contractDate: toInputDate(new Date()),
    signatureDate: toInputDate(new Date())
  })
  const [properties, setProperties] = useState([])
  const [filteredProperties, setFilteredProperties] = useState([])
  const [propertySearch, setPropertySearch] = useState('')
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false)
  const [tenants, setTenants] = useState([])
  const [filteredTenants, setFilteredTenants] = useState([])
  const [tenantSearch, setTenantSearch] = useState('')
  const [showTenantDropdown, setShowTenantDropdown] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [noTenantsMessage, setNoTenantsMessage] = useState('')
  const [currentUserProfile, setCurrentUserProfile] = useState(null)
  const canEditTenant = !isEdit || !formData.tenant_id

  useEffect(() => {
    const fetchCurrentProfile = async () => {
      try {
        const response = await userService.getById()
        const profile = response.data?.data || response.data || null
        if (profile) {
          setCurrentUserProfile(profile)
          setFormData(prev => ({
            ...prev,
            nombresApellidosArrendador: prev.nombresApellidosArrendador || getFullName(profile),
            landlordNotificationAddress: prev.landlordNotificationAddress || profile.address || ''
          }))
        }
      } catch (err) {
        console.error('Error fetching current user profile:', err)
      }
    }

    fetchCurrentProfile()
  }, [])

  const loadTenantsForProperty = async (property, currentTenantId = '') => {
    if (!property?.invitation_code) {
      setTenants([])
      setFilteredTenants([])
      setNoTenantsMessage('Esta propiedad no tiene un código de invitación. Los inquilinos deben tener el código de invitación para aparecer aquí.')
      if (!currentTenantId) {
        setFormData(prev => ({ ...prev, tenant_id: '' }))
        setTenantSearch('')
      }
      return []
    }

    try {
      const response = await userService.getTenantsByPropertyCode(property.id)
      const tenantsForProperty = response.data?.data || []
      setTenants(tenantsForProperty)
      setFilteredTenants(tenantsForProperty)
      setNoTenantsMessage(tenantsForProperty.length === 0 ? 'No hay inquilinos vinculados a esta propiedad.' : '')

      if (currentTenantId) {
        const currentTenant = tenantsForProperty.find(t => (t.identity_user_id || t.id) === currentTenantId)
        if (!currentTenant) {
          setFormData(prev => ({ ...prev, tenant_id: '' }))
          setTenantSearch('')
        }
      }

      return tenantsForProperty
    } catch (err) {
      console.error('Error fetching tenants by property:', err)
      setTenants([])
      setFilteredTenants([])
      setNoTenantsMessage('Error al cargar inquilinos')
      return []
    }
  }

  // Cargar propiedades al montar el componente
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyService.getAll()
        console.log('Propiedades cargadas:', response.data)
        
        // Manejar diferentes formatos de respuesta de la API
        let propertyList = []
        if (response.data && response.data.data) {
          // Formato con paginación: { data: { properties: [...] } }
          if (response.data.data.properties) {
            propertyList = response.data.data.properties
          } 
          // Formato sin paginación: { data: [...] }
          else if (Array.isArray(response.data.data)) {
            propertyList = response.data.data
          }
        } 
        // Formato directo: { data: [...] }
        else if (response.data && Array.isArray(response.data)) {
          propertyList = response.data
        }

        if (propertyList.length > 0) {
          console.log('Se cargaron', propertyList.length, 'propiedades')
          setProperties(propertyList)
          setFilteredProperties(propertyList)
        } else {
          console.warn('No hay propiedades disponibles. Por favor crea una propiedad primero.')
          setProperties([])
          setFilteredProperties([])
        }
      } catch (err) {
        console.error('Error al cargar propiedades:', err)
        setProperties([])
        setFilteredProperties([])
      }
    }
    fetchProperties()
  }, [])

  // Cargar inquilinos al montar el componente
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        // Obtener inquilinos desde la API
        const response = await userService.getTenants()
        console.log('Inquilinos cargados:', response.data)
        if (response.data && response.data.data && response.data.data.length > 0) {
          setTenants(response.data.data)
          setFilteredTenants(response.data.data)
        } else if (response.data && response.data.length > 0) {
          setTenants(response.data)
          setFilteredTenants(response.data)
        } else {
          console.warn('No hay inquilinos disponibles. Por favor registra un inquilino primero.')
          setTenants([])
          setFilteredTenants([])
        }
      } catch (err) {
        console.error('Error al cargar inquilinos:', err)
        setTenants([])
        setFilteredTenants([])
      }
    }
    fetchTenants()
  }, [])

  // Cargar contrato existente si estamos editando
  useEffect(() => {
    if (!id) return
    
    const fetchContract = async () => {
      setLoading(true)
      try {
        const response = await contractService.getById(id)
        // Handle different response formats
        const contractData = response.data?.data?.contract || response.data?.data || response.data
        console.log('Contrato cargado:', contractData)
        
        if (!contractData) {
          throw new Error('No se encontró el contrato')
        }
        
        // Formatear fechas para input date
        const startDate = toInputDate(contractData.start_date || contractData.startDate)
        const endDate = toInputDate(contractData.end_date || contractData.endDate)
        
        // Usar los datos de la propiedad e inquilino incluidos en el contrato si están disponibles
        const propertyFromContract = contractData.property
        const tenantFromContract = contractData.tenant
        
        setFormData({
          property_id: contractData.property_id || '',
          tenant_id: contractData.tenant_id || '',
          start_date: startDate,
          end_date: endDate,
          contractDate: toInputDate(contractData.contract_date || contractData.created_at || contractData.createdAt),
          monthly_rent: contractData.monthly_rent || '',
          security_deposit: contractData.security_deposit || '',
          payment_frequency: contractData.payment_frequency || 'monthly',
          payment_day: contractData.payment_day || 1,
          terms_conditions: contractData.terms_conditions || '',
          special_conditions: contractData.special_conditions || '',
          nombresApellidosArrendador: contractData.nombres_apellidos_arrendador || contractData.nombresApellidosArrendador || getFullName(currentUserProfile),
          contractDomicile: contractData.contract_domicile || '',
          propertyPurpose: contractData.property_purpose || '',
          increasePercentage: contractData.increase_percentage || '',
          propertyInstallations: contractData.property_installations || contractData.instalaciones_inmueble || contractData.instalacionesInmueble || '',
          tenantDocumentNumber: contractData.tenant_document_number || contractData.tenantDocumentNumber || '',
          debtorFullName: contractData.debtor_full_name || '',
          debtorDocumentNumber: contractData.debtor_document_number || '',
          debtorIssuePlace: contractData.debtor_issue_place || '',
          debtorAddress: contractData.debtor_address || '',
          debtor2FullName: contractData.debtor2_full_name || '',
          debtor2DocumentNumber: contractData.debtor2_document_number || contractData.debtor2DocumentNumber || '',
          debtor2IssuePlace: contractData.debtor2_issue_place || '',
          debtor2Address: contractData.debtor2_address || '',
          landlordNotificationAddress: contractData.landlord_notification_address || currentUserProfile?.address || '',
          signatureDate: toInputDate(contractData.signature_date || contractData.updated_at || contractData.updatedAt)
        })
        
        // Set search fields to show selected property/tenant names
        if (propertyFromContract) {
          setSelectedProperty(propertyFromContract)
          setPropertySearch(`${propertyFromContract.name} - ${propertyFromContract.address}`)
          await loadTenantsForProperty(propertyFromContract, contractData.tenant_id || '')
        } else if (properties.length > 0) {
          const property = properties.find(p => p.id === contractData.property_id)
          if (property) {
            setSelectedProperty(property)
            setPropertySearch(`${property.name} - ${property.address}`)
            await loadTenantsForProperty(property, contractData.tenant_id || '')
          }
        }
        
        if (tenantFromContract) {
          const tenantName = tenantFromContract.name || `${tenantFromContract.first_name} ${tenantFromContract.last_name}`
          setTenantSearch(tenantName)
        } else if (tenants.length > 0) {
          const tenant = tenants.find(t => (t.identity_user_id || t.id) === contractData.tenant_id)
          if (tenant) {
            setTenantSearch(tenant.name || `${tenant.first_name} ${tenant.last_name}`)
          }
        }
      } catch (err) {
        console.error('Error al cargar contrato:', err)
        setError('Error al cargar el contrato: ' + err.message)
      } finally {
        setLoading(false)
      }
    }
    
    // Esperar a que se carguen propiedades e inquilinos
    if (properties.length > 0 && tenants.length > 0) {
      fetchContract()
    } else {
      // Cargar propiedades e inquilinos primero si no están cargados
      const loadData = async () => {
        try {
          const [propsRes, tenantsRes] = await Promise.all([
            propertyService.getAll(),
            userService.getTenants()
          ])
          
          let propertyList = propsRes.data?.data?.properties || propsRes.data?.data || propsRes.data || []
          setProperties(propertyList)
          setFilteredProperties(propertyList)
          
          let tenantList = tenantsRes.data?.data || tenantsRes.data || []
          setTenants(tenantList)
          setFilteredTenants(tenantList)
          
          // Luego cargar el contrato
          await fetchContract()
        } catch (err) {
          console.error('Error al cargar datos:', err)
        }
      }
      loadData()
    }
  }, [id])

  // Filtrar propiedades por nombre o dirección
  useEffect(() => {
    if (propertySearch.trim() === '') {
      setFilteredProperties(properties)
    } else {
      const filtered = properties.filter(property => 
        property.name?.toLowerCase().includes(propertySearch.toLowerCase()) ||
        property.address?.toLowerCase().includes(propertySearch.toLowerCase())
      )
      setFilteredProperties(filtered)
    }
  }, [propertySearch, properties])

  // Filtrar inquilinos por nombre
  useEffect(() => {
    if (tenantSearch.trim() === '') {
      setFilteredTenants(tenants)
    } else {
      const filtered = tenants.filter(tenant => 
        (tenant.name?.toLowerCase() || '').includes(tenantSearch.toLowerCase()) ||
        (tenant.email?.toLowerCase() || '').includes(tenantSearch.toLowerCase())
      )
      setFilteredTenants(filtered)
    }
  }, [tenantSearch, tenants])

  const handlePropertySelect = async (property) => {
    setSelectedProperty(property)
      setFormData({ 
        ...formData, 
        property_id: property.id,
        monthly_rent: property.monthly_rent || formData.monthly_rent,
        contractDomicile: formData.contractDomicile || getPropertyLocation(property)
      })
    setPropertySearch(`${property.name} - ${property.address}`)
    setShowPropertyDropdown(false)
    
    // Fetch tenants filtered by property invitation code
    if (property.invitation_code) {
      try {
        const response = await userService.getTenantsByPropertyCode(property.id)
        const filteredTenants = response.data?.data || []
        setTenants(filteredTenants)
        setFilteredTenants(filteredTenants)
        setNoTenantsMessage('')
        
        // Clear tenant selection if current tenant doesn't have this property's code
        if (formData.tenant_id) {
          const currentTenant = filteredTenants.find(t => (t.identity_user_id || t.id) === formData.tenant_id)
          if (!currentTenant) {
            setFormData(prev => ({ ...prev, tenant_id: '' }))
            setTenantSearch('')
          }
        }
      } catch (err) {
        console.error('Error fetching tenants by property:', err)
        setTenants([])
        setFilteredTenants([])
        setNoTenantsMessage('Error al cargar inquilinos')
      }
    } else {
      // Property doesn't have an invitation code, no tenants can be selected
      setTenants([])
      setFilteredTenants([])
      setFormData(prev => ({ ...prev, tenant_id: '' }))
      setTenantSearch('')
      setNoTenantsMessage('Esta propiedad no tiene un código de invitación. Los inquilinos deben tener el código de invitación para aparecer aquí.')
    }
  }

  const handlePropertySearchChange = (e) => {
    setPropertySearch(e.target.value)
    setShowPropertyDropdown(true)
    if (!e.target.value) {
      setSelectedProperty(null)
      setFormData({ ...formData, property_id: '', monthly_rent: '', contractDomicile: '' })
    }
  }

  const handleTenantSelect = (tenant) => {
    if (!tenant.identity_user_id) {
      setError('El inquilino seleccionado no está vinculado a un usuario válido de RentalBiz.')
      return
    }

    const tenantFullName = tenant.name || `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim()
    setFormData({
      ...formData,
      tenant_id: tenant.identity_user_id || tenant.id,
      tenantDocumentNumber: formData.tenantDocumentNumber || tenant.document_number || tenant.documentNumber || '',
      debtorFullName: formData.debtorFullName || tenantFullName,
      debtorAddress: formData.debtorAddress || tenant.address || ''
    })
    setTenantSearch(tenant.name || `${tenant.first_name} ${tenant.last_name}`)
    setShowTenantDropdown(false)
  }

  const handleTenantSearchChange = (e) => {
    setTenantSearch(e.target.value)
    setShowTenantDropdown(true)
    if (!e.target.value) {
      setFormData({
        ...formData,
        tenant_id: '',
        tenantDocumentNumber: '',
        debtorFullName: '',
        debtorAddress: ''
      })
    }
  }

  // Cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPropertyDropdown && !event.target.closest('.property-search-container')) {
        setShowPropertyDropdown(false)
      }
      if (showTenantDropdown && !event.target.closest('.tenant-search-container')) {
        setShowTenantDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showPropertyDropdown, showTenantDropdown])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setError('')
  }

  const selectedPropertyData = selectedProperty || properties.find(property => property.id === formData.property_id) || null
  const selectedTenantData = tenants.find(tenant => (tenant.identity_user_id || tenant.id) === formData.tenant_id) || null
  const landlordFullName = getFullName(currentUserProfile)
  const tenantFullName = getFullName(selectedTenantData) || tenantSearch
  const propertyLocation = getPropertyLocation(selectedPropertyData)
  const formattedPeriod = formatContractPeriod(formData.start_date, formData.end_date)
  const durationMonths = calculateMonthDifference(formData.start_date, formData.end_date)
  const paymentDayText = getPaymentDayText(formData.payment_day)
  const signatureDateText = formatSpanishDate(formData.signatureDate)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')

    // Validar que hay propiedades e inquilinos disponibles
    if (properties.length === 0) {
      setError('No hay propiedades disponibles. Por favor crea una propiedad primero.')
      setSaving(false)
      return
    }
    if (!isEdit && tenants.length === 0) {
      setError('No hay inquilinos disponibles. Por favor registra un inquilino primero.')
      setSaving(false)
      return
    }

    // Validar que se ha seleccionado una propiedad y un inquilino
    if (!formData.property_id) {
      setError('Por favor selecciona una propiedad.')
      setSaving(false)
      return
    }
    if (!formData.tenant_id) {
      setError('Por favor selecciona un inquilino.')
      setSaving(false)
      return
    }

    try {
      const contractPayload = {
        ...formData,
        instalacionesInmueble: formData.propertyInstallations
      }

      if (isEdit) {
        await contractService.update(id, contractPayload)
        navigate('/rentalbiz/contracts')
      } else {
        await contractService.create(contractPayload)
        navigate('/rentalbiz/contracts')
      }
    } catch (err) {
      const errorData = err.response?.data
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        // Mostrar errores de validación específicos
        const errorMessages = errorData.errors.map(e => `${e.field}: ${e.message}`).join('\n')
        setError(errorMessages)
      } else {
        setError(err.response?.data?.message || `Error al ${isEdit ? 'actualizar' : 'crear'} el contrato`)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 animate-fade-in">
      <div className="flex items-center space-x-4">
        <Link to="/rentalbiz/contracts" className="text-gray-500 hover:text-gray-700">
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Editar Contrato' : 'Nuevo Contrato'}
          </h1>
          <p className="text-gray-500">
            {isEdit ? 'Modifica los datos del contrato' : 'Crea un nuevo contrato de arrendamiento'}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 whitespace-pre-line">
              {error}
            </div>
          )}

          <section className="card space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Información general</h2>
              <p className="mt-1 text-sm text-gray-500">
                Selecciona la propiedad, el inquilino y los datos principales del contrato.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="relative property-search-container">
                <label htmlFor="property_search" className="label">Propiedad</label>
                <input
                  id="property_search"
                  name="property_search"
                  type="text"
                  value={propertySearch}
                  onChange={handlePropertySearchChange}
                  onFocus={() => setShowPropertyDropdown(true)}
                  className="input"
                  placeholder="Buscar por nombre o dirección"
                  autoComplete="off"
                  required
                  disabled={isEdit}
                />
                {showPropertyDropdown && filteredProperties.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {filteredProperties.map(property => (
                      <div
                        key={property.id}
                        className="cursor-pointer px-4 py-2 hover:bg-gray-50"
                        onClick={() => handlePropertySelect(property)}
                      >
                        <div className="font-medium">{property.name}</div>
                        <div className="text-sm text-gray-500">{property.address}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative tenant-search-container">
                <label htmlFor="tenant_search" className="label">Inquilino</label>
                <input
                  id="tenant_search"
                  name="tenant_search"
                  type="text"
                  value={tenantSearch}
                  onChange={handleTenantSearchChange}
                  onFocus={() => setShowTenantDropdown(true)}
                  className="input"
                  placeholder="Buscar por nombre o email"
                  autoComplete="off"
                  required
                  disabled={!canEditTenant}
                />
                {noTenantsMessage && (
                  <div className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700">
                    {noTenantsMessage}
                  </div>
                )}
                {showTenantDropdown && filteredTenants.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {filteredTenants.map(tenant => {
                      const tenantName = tenant.name || `${tenant.first_name} ${tenant.last_name}`
                      const isSelectable = Boolean(tenant.identity_user_id)
                      return (
                        <div
                          key={tenant.id}
                          className={`px-4 py-2 ${isSelectable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed bg-gray-50 text-gray-400'}`}
                          onClick={() => isSelectable && handleTenantSelect(tenant)}
                        >
                          <div className="font-medium">{tenantName}</div>
                          <div className="text-sm text-gray-500">{tenant.email}</div>
                          {!isSelectable && (
                            <div className="mt-1 text-xs text-amber-700">
                              No disponible para contratos: falta vínculo con usuario.
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="tenantDocumentNumber" className="label">Número de documento del arrendatario</label>
              <input
                id="tenantDocumentNumber"
                name="tenantDocumentNumber"
                type="text"
                value={formData.tenantDocumentNumber}
                onChange={handleChange}
                className="input"
                placeholder="Documento de identidad del arrendatario"
              />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="start_date" className="label">Fecha de inicio</label>
                <input
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleChange}
                  className="input"
                  required
                />
              </div>

              <div>
                <label htmlFor="end_date" className="label">Fecha de fin</label>
                <input
                  id="end_date"
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleChange}
                  className="input"
                  required
                />
              </div>

              <div>
                <label htmlFor="contractDate" className="label">Fecha del contrato</label>
                <input
                  id="contractDate"
                  name="contractDate"
                  type="date"
                  value={formData.contractDate}
                  onChange={handleChange}
                  className="input"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="nombresApellidosArrendador" className="label">Nombres y apellidos del arrendador</label>
                <input
                  id="nombresApellidosArrendador"
                  name="nombresApellidosArrendador"
                  type="text"
                  value={formData.nombresApellidosArrendador}
                  onChange={handleChange}
                  className="input"
                  placeholder="Se toma del perfil del arrendador si está disponible"
                />
              </div>

              <div>
                <label htmlFor="payment_frequency" className="label">Frecuencia de pago</label>
                <select
                  id="payment_frequency"
                  name="payment_frequency"
                  value={formData.payment_frequency}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="monthly">Mensual</option>
                  <option value="bimestral">Bimestral</option>
                  <option value="quarterly">Trimestral</option>
                </select>
              </div>

              <div>
                <label htmlFor="monthly_rent" className="label">Renta mensual ($)</label>
                <input
                  id="monthly_rent"
                  name="monthly_rent"
                  type="number"
                  value={formData.monthly_rent}
                  onChange={handleChange}
                  className="input"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label htmlFor="security_deposit" className="label">Depósito de seguridad ($)</label>
                <input
                  id="security_deposit"
                  name="security_deposit"
                  type="number"
                  value={formData.security_deposit}
                  onChange={handleChange}
                  className="input"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </section>

          <section className="card space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Condiciones comerciales</h2>
              <p className="mt-1 text-sm text-gray-500">
                Define la ubicación contractual, el destino del inmueble y los campos que alimentan la plantilla.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="payment_day" className="label">Número de día de pago</label>
                <input
                  id="payment_day"
                  name="payment_day"
                  type="number"
                  value={formData.payment_day}
                  onChange={handleChange}
                  className="input"
                  min="1"
                  max="28"
                  required
                />
              </div>

              <div>
                <label htmlFor="increasePercentage" className="label">Porcentaje de incremento</label>
                <input
                  id="increasePercentage"
                  name="increasePercentage"
                  type="text"
                  value={formData.increasePercentage}
                  onChange={handleChange}
                  className="input"
                  placeholder="Ej. 10%"
                />
              </div>

              <div>
                <label htmlFor="contractDomicile" className="label">Domicilio del contrato</label>
                <input
                  id="contractDomicile"
                  name="contractDomicile"
                  type="text"
                  value={formData.contractDomicile}
                  onChange={handleChange}
                  className="input"
                  placeholder="Dirección donde se celebra el contrato"
                />
              </div>

              <div>
                <label htmlFor="propertyPurpose" className="label">Destino del inmueble</label>
                <input
                  id="propertyPurpose"
                  name="propertyPurpose"
                  type="text"
                  value={formData.propertyPurpose}
                  onChange={handleChange}
                  className="input"
                  placeholder="Ej. Vivienda, oficina, local, bodega"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="propertyInstallations" className="label">Instalaciones del inmueble</label>
                <textarea
                  id="propertyInstallations"
                  name="propertyInstallations"
                  value={formData.propertyInstallations}
                  onChange={handleChange}
                  className="input"
                  rows={4}
                  placeholder="Describe las instalaciones, áreas o elementos incluidos en el inmueble"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="landlordNotificationAddress" className="label">Dirección de notificaciones del arrendador</label>
                <input
                  id="landlordNotificationAddress"
                  name="landlordNotificationAddress"
                  type="text"
                  value={formData.landlordNotificationAddress}
                  onChange={handleChange}
                  className="input"
                  placeholder="Tomada del perfil del sistema si ya existe"
                />
              </div>
            </div>
          </section>

          <section className="card space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Deudores</h2>
              <p className="mt-1 text-sm text-gray-500">
                Completa el deudor principal y, si aplica, el segundo obligado.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="debtorFullName" className="label">Nombres y apellidos del deudor</label>
                <input
                  id="debtorFullName"
                  name="debtorFullName"
                  type="text"
                  value={formData.debtorFullName}
                  onChange={handleChange}
                  className="input"
                  placeholder="Nombre completo del deudor principal"
                />
              </div>

              <div>
                <label htmlFor="debtorDocumentNumber" className="label">Número de documento del deudor</label>
                <input
                  id="debtorDocumentNumber"
                  name="debtorDocumentNumber"
                  type="text"
                  value={formData.debtorDocumentNumber}
                  onChange={handleChange}
                  className="input"
                  placeholder="Documento de identidad"
                />
              </div>

              <div>
                <label htmlFor="debtorIssuePlace" className="label">Lugar de expedición del deudor</label>
                <input
                  id="debtorIssuePlace"
                  name="debtorIssuePlace"
                  type="text"
                  value={formData.debtorIssuePlace}
                  onChange={handleChange}
                  className="input"
                  placeholder="Ciudad o municipio"
                />
              </div>

              <div>
                <label htmlFor="debtorAddress" className="label">Domicilio del deudor</label>
                <input
                  id="debtorAddress"
                  name="debtorAddress"
                  type="text"
                  value={formData.debtorAddress}
                  onChange={handleChange}
                  className="input"
                  placeholder="Dirección del deudor"
                />
              </div>

              <div>
                <label htmlFor="debtor2FullName" className="label">Nombres y apellidos del deudor 1</label>
                <input
                  id="debtor2FullName"
                  name="debtor2FullName"
                  type="text"
                  value={formData.debtor2FullName}
                  onChange={handleChange}
                  className="input"
                  placeholder="Segundo obligado, si aplica"
                />
              </div>

              <div>
                <label htmlFor="debtor2DocumentNumber" className="label">Número de documento del deudor 1</label>
                <input
                  id="debtor2DocumentNumber"
                  name="debtor2DocumentNumber"
                  type="text"
                  value={formData.debtor2DocumentNumber}
                  onChange={handleChange}
                  className="input"
                  placeholder="Documento de identidad"
                />
              </div>

              <div>
                <label htmlFor="debtor2IssuePlace" className="label">Lugar de expedición del deudor 1</label>
                <input
                  id="debtor2IssuePlace"
                  name="debtor2IssuePlace"
                  type="text"
                  value={formData.debtor2IssuePlace}
                  onChange={handleChange}
                  className="input"
                  placeholder="Ciudad o municipio"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="debtor2Address" className="label">Domicilio del deudor 1</label>
                <input
                  id="debtor2Address"
                  name="debtor2Address"
                  type="text"
                  value={formData.debtor2Address}
                  onChange={handleChange}
                  className="input"
                  placeholder="Dirección del segundo obligado"
                />
              </div>
            </div>
          </section>

          <section className="card space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Términos y condiciones</h2>
              <p className="mt-1 text-sm text-gray-500">
                Agrega aquí las cláusulas generales y especiales del contrato.
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="terms_conditions" className="label">Términos generales</label>
                <textarea
                  id="terms_conditions"
                  name="terms_conditions"
                  value={formData.terms_conditions}
                  onChange={handleChange}
                  className="input"
                  rows={5}
                />
              </div>

              <div>
                <label htmlFor="special_conditions" className="label">Condiciones especiales</label>
                <textarea
                  id="special_conditions"
                  name="special_conditions"
                  value={formData.special_conditions}
                  onChange={handleChange}
                  className="input"
                  rows={5}
                />
              </div>
            </div>
          </section>

          <div className="flex justify-end space-x-4">
            <Link to="/rentalbiz/contracts" className="btn btn-secondary">Cancelar</Link>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary disabled:opacity-50"
            >
              {saving ? 'Guardando...' : isEdit ? 'Actualizar Contrato' : 'Crear Contrato'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

export default ContractForm
